import {
  ADMINISTRATION_OUTCOMES,
  DOSE_STATES,
  DOSE_TIMINGS,
  EMAR_REASON_MAX,
  EMAR_WINDOW_MAX_MINUTES,
  MEDICATION_SUPPLIES,
} from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, RECORD_STATUSES } from './clinical-common.ts';
import { acknowledgementSchema, allergyFindingSchema } from './dispensing.ts';

// eMAR and inpatient pharmacy (docs/plans/phase2-emar-inpatient-pharmacy.md §3). Reasons and
// notes are PHI-adjacent: they travel only in bodies, never in URLs, logs or audit metadata.

const windowMinutes = z.number().int().min(0).max(EMAR_WINDOW_MAX_MINUTES).nullable();

export const emarSettingsSchema = z.object({
  /** Minutes before the due time a dose is not early; null: not set, nothing is early. */
  earlyWindowMinutes: windowMinutes,
  /** Minutes after the due time a dose is not late; null: not set, nothing is late or overdue. */
  lateWindowMinutes: windowMinutes,
});
export type EmarSettings = z.infer<typeof emarSettingsSchema>;

// --- Verification ------------------------------------------------------------------------------

export const verificationSchema = z.object({
  id: z.uuid(),
  supply: z.enum(MEDICATION_SUPPLIES),
  note: z.string().nullable(),
  verifiedAt: z.iso.datetime(),
  verifiedBy: actorSchema,
  status: z.enum(RECORD_STATUSES),
  statusReason: z.string().nullable(),
});
export type VerificationView = z.infer<typeof verificationSchema>;

export const verifyOrderBodySchema = z.object({
  supply: z.enum(MEDICATION_SUPPLIES),
  /** The line version the pharmacist saw; a stopped or changed line refuses. */
  expectedVersion: z.number().int().positive(),
  note: z.string().trim().min(1).max(500).optional(),
});
export type VerifyOrderBody = z.infer<typeof verifyOrderBodySchema>;

export const wardOrderSchema = z.object({
  lineId: z.uuid(),
  lineVersion: z.number().int().positive(),
  prescriptionId: z.uuid(),
  prescriptionNumber: z.string(),
  admissionId: z.uuid(),
  patient: z.object({ id: z.uuid(), displayName: z.string(), patientNumber: z.string() }),
  ward: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  bedLabel: z.string().nullable(),
  drugDisplay: z.string(),
  doseDisplay: z.string(),
  signedAt: z.iso.datetime(),
  prescriberDisplay: z.string(),
  isControlled: z.boolean(),
  quantityPrescribed: z.number().int(),
  dispensed: z.number().int(),
  /** Still to dispense: prescribed less dispensed, prepared and closed. */
  remaining: z.number().int(),
  verification: verificationSchema.nullable(),
});
export type WardOrder = z.infer<typeof wardOrderSchema>;

export const wardOrdersSchema = z.object({ orders: z.array(wardOrderSchema) });
export type WardOrders = z.infer<typeof wardOrdersSchema>;

// --- The medicines chart -----------------------------------------------------------------------

export const administrationSchema = z.object({
  id: z.uuid(),
  prescriptionLineId: z.uuid(),
  drugDisplay: z.string(),
  doseDisplay: z.string(),
  /** Null for an "as needed" dose. */
  scheduledFor: z.iso.datetime().nullable(),
  outcome: z.enum(ADMINISTRATION_OUTCOMES),
  reason: z.string().nullable(),
  administeredAt: z.iso.datetime(),
  administeredBy: actorSchema,
  recordedAt: z.iso.datetime(),
  timing: z.enum(DOSE_TIMINGS),
  minutesFromDue: z.number().int().nullable(),
  supply: z.enum(MEDICATION_SUPPLIES).nullable(),
  wardStockQuantity: z.number().int().nullable(),
  status: z.enum(RECORD_STATUSES),
  statusReason: z.string().nullable(),
});
export type AdministrationView = z.infer<typeof administrationSchema>;

export const doseSchema = z.object({
  scheduledFor: z.iso.datetime(),
  state: z.enum(DOSE_STATES),
  /** The active record of this dose, when there is one. */
  administrationId: z.uuid().nullable(),
});
export type DoseView = z.infer<typeof doseSchema>;

export const medicationOrderSchema = z.object({
  lineId: z.uuid(),
  lineVersion: z.number().int().positive(),
  prescriptionNumber: z.string(),
  status: z.enum(['active', 'stopped', 'cancelled', 'entered_in_error']),
  statusReason: z.string().nullable(),
  statusChangedAt: z.iso.datetime().nullable(),
  drugDisplay: z.string(),
  /** "1 capsule, By mouth, Three times a day": the words captured at signing. */
  doseDisplay: z.string(),
  instructions: z.string().nullable(),
  frequencyKind: z.enum(['regular', 'as_needed', 'once']),
  /** As needed only: the prescriber's maximum and how many were given in the last 24 hours. */
  maxDosesPerDay: z.number().int().nullable(),
  givenInLastDay: z.number().int(),
  signedAt: z.iso.datetime(),
  prescriberDisplay: z.string(),
  isControlled: z.boolean(),
  /** The stock unit a ward-stock dose is counted in ("capsule"). */
  stockUnit: z.string().nullable(),
  verification: verificationSchema.nullable(),
  /** The allergy check against today's record, for the medicine prescribed. */
  allergyFindings: z.array(allergyFindingSchema),
  /** The scheduled doses in the chart window, oldest first. */
  doses: z.array(doseSchema),
});
export type MedicationOrderView = z.infer<typeof medicationOrderSchema>;

export const medicationChartSchema = z.object({
  admission: z.object({
    id: z.uuid(),
    patientId: z.uuid(),
    visitId: z.uuid(),
    status: z.enum(['admitted', 'discharged', 'cancelled']),
    admittedAt: z.iso.datetime(),
    ward: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    bedLabel: z.string().nullable(),
  }),
  windows: emarSettingsSchema,
  /** The ward's stock store, when it keeps one. */
  wardStock: z.object({ storeId: z.uuid(), name: z.string() }).nullable(),
  /** The chart shows doses from `from` to `to`. */
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  orders: z.array(medicationOrderSchema),
  /** Every dose recorded in the stay, newest first, entered in error included. */
  administrations: z.array(administrationSchema),
});
export type MedicationChart = z.infer<typeof medicationChartSchema>;

export const recordAdministrationBodySchema = z.object({
  /** Generated once per record; a retried request returns the chart unchanged. */
  clientRequestId: z.uuid(),
  prescriptionLineId: z.uuid(),
  /** The due time of the dose, from the chart; null for an "as needed" dose. */
  scheduledFor: z.iso.datetime().nullable(),
  outcome: z.enum(ADMINISTRATION_OUTCOMES),
  reason: z
    .string()
    .max(EMAR_REASON_MAX * 2)
    .optional(),
  /** When it happened; absent means now. */
  administeredAt: z.iso.datetime().optional(),
  /** Ward stock only: whole stock units taken from the ward's store. */
  wardStockQuantity: z.number().int().positive().max(1000).optional(),
  acknowledgements: z.array(acknowledgementSchema).max(20).default([]),
});
export type RecordAdministrationBody = z.input<typeof recordAdministrationBodySchema>;
